import { extensionOf } from "@/analytics/hygiene";
import {
  trackCanvasRestored,
  trackChatRestored,
  trackFileRestored,
  trackSkillRestored,
  trackWorkspaceRestored,
} from "@/utils/analytics";
import { isSkillNameTaken } from "@/utils/skillNameConflict";
import {
  ArchivedCanvasListResponse,
  ArchivedFileRecordListResponse,
  ArchivedFlowSessionListResponse,
  ArchivedSkillListResponse,
  ArchivedWorkspaceListResponse,
  Canvas,
  FileRecord,
  Skill,
  Workspace,
} from "@cohesive/api-client";
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

const ARCHIVED_PAGE_SIZE = 50;

/** Cursor pagination helpers shared by the four archived-list endpoints. */
const archivedInfiniteOptions = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (page: {
    pagination: { has_more: boolean; next_ts?: string };
  }) => (page.pagination.has_more ? page.pagination.next_ts : undefined),
};

/**
 * Extract the server-provided user message from a failed archive/restore call.
 * Restore endpoints return 409 with actionable guidance ("Restore the
 * Workspace first.") that should surface verbatim instead of a generic toast.
 *
 * The dashboard's axios response interceptor flattens AxiosError into ApiError
 * before this runs, moving the parsed body onto `.data` (not `.response.data`).
 * We read the ApiError shape first, then fall back to a raw AxiosError.
 */
export function restoreErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    data?: { error?: { message?: string } };
    response?: { data?: { error?: { message?: string } } };
  };
  return (
    e?.data?.error?.message || e?.response?.data?.error?.message || fallback
  );
}

/**
 * Invalidate every cache that can contain archived/restored resources.
 * Used by both the delete mutations (items are archived) and the restore
 * mutations (items leave the archive).
 */
export function invalidateArchivedQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["archived"] });
  queryClient.invalidateQueries({ queryKey: ["canvases"] });
  queryClient.invalidateQueries({ queryKey: ["canvas"] });
  queryClient.invalidateQueries({ queryKey: ["workspaces"] });
  queryClient.invalidateQueries({ queryKey: ["workspace"] });
  queryClient.invalidateQueries({ queryKey: ["workspace-flow-sessions"] });
  queryClient.invalidateQueries({ queryKey: ["canvas-flow-sessions"] });
  queryClient.invalidateQueries({ queryKey: ["flowSession"] });
  queryClient.invalidateQueries({ queryKey: ["fileRecords"] });
  queryClient.invalidateQueries({ queryKey: ["skills"] });
  queryClient.invalidateQueries({ queryKey: ["skill"] });
}

export function useArchivedWorkspaces() {
  return useInfiniteQuery<ArchivedWorkspaceListResponse, AxiosError>({
    queryKey: ["archived", "workspaces"],
    queryFn: ({ pageParam }) =>
      orgAPI.workspaces.listArchived({
        limit: ARCHIVED_PAGE_SIZE,
        before_ts: pageParam as string | undefined,
      }),
    ...archivedInfiniteOptions,
  });
}

export function useArchivedCanvases() {
  return useInfiniteQuery<ArchivedCanvasListResponse, AxiosError>({
    queryKey: ["archived", "canvases"],
    queryFn: ({ pageParam }) =>
      orgAPI.canvas.listArchived({
        limit: ARCHIVED_PAGE_SIZE,
        before_ts: pageParam as string | undefined,
      }),
    ...archivedInfiniteOptions,
  });
}

export function useArchivedChats() {
  return useInfiniteQuery<ArchivedFlowSessionListResponse, AxiosError>({
    queryKey: ["archived", "chats"],
    queryFn: ({ pageParam }) =>
      orgAPI.flowSessions.listArchived({
        limit: ARCHIVED_PAGE_SIZE,
        before_ts: pageParam as string | undefined,
      }),
    ...archivedInfiniteOptions,
  });
}

export function useArchivedFiles() {
  return useInfiniteQuery<ArchivedFileRecordListResponse, AxiosError>({
    queryKey: ["archived", "files"],
    queryFn: ({ pageParam }) =>
      orgAPI.fileRecords.listArchived({
        limit: ARCHIVED_PAGE_SIZE,
        before_ts: pageParam as string | undefined,
      }),
    ...archivedInfiniteOptions,
  });
}

export function useArchivedSkills() {
  return useInfiniteQuery<ArchivedSkillListResponse, AxiosError>({
    queryKey: ["archived", "skills"],
    queryFn: ({ pageParam }) =>
      orgAPI.skills.listArchived({
        limit: ARCHIVED_PAGE_SIZE,
        before_ts: pageParam as string | undefined,
      }),
    ...archivedInfiniteOptions,
  });
}

export function useRestoreWorkspace(
  options?: UseMutationOptions<Workspace, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => orgAPI.workspaces.restore(workspaceId),
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      invalidateArchivedQueries(queryClient);
      trackWorkspaceRestored({
        workspaceId: data.id,
        isPrivate: data.is_private,
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useRestoreCanvas(
  options?: UseMutationOptions<Canvas, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasId: string) => orgAPI.canvas.restore(canvasId),
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      invalidateArchivedQueries(queryClient);
      trackCanvasRestored({
        canvasId: data.id,
        workspaceId: data.workspace_id,
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useRestoreFlowSession(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (flowSessionId: string) =>
      orgAPI.flowSessions.restore(flowSessionId),
    ...options,
    onSuccess: (...args) => {
      const [, flowSessionId] = args;
      invalidateArchivedQueries(queryClient);
      trackChatRestored({ flowSessionId });
      options?.onSuccess?.(...args);
    },
  });
}

export type RestoreSkillVariables = {
  skillId: string;
  newName?: string;
};

/**
 * Message for a failed restore from the "Moved to Trash" undo toast. A name
 * collision can't be resolved inline there, so point the user at the Trash
 * page's keep-both flow instead of a generic error.
 */
export function undoRestoreSkillErrorMessage(err: unknown): string {
  return isSkillNameTaken(err)
    ? "A skill with that name now exists. Open Trash to restore it as a copy."
    : "Failed to restore skill.";
}

export function useRestoreSkill(
  options?: UseMutationOptions<Skill, AxiosError, RestoreSkillVariables>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, newName }: RestoreSkillVariables) =>
      orgAPI.skills.restore(
        skillId,
        newName ? { new_name: newName } : undefined,
      ),
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      invalidateArchivedQueries(queryClient);
      trackSkillRestored({ skillId: data.id });
      options?.onSuccess?.(...args);
    },
  });
}

/** Recovers a soft-deleted S3 file from the Trash Files tab (14-day window). */
export function useRecoverFileRecord(
  options?: UseMutationOptions<FileRecord, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.recover(fileRecordId),
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      invalidateArchivedQueries(queryClient);
      trackFileRestored({
        fileRecordId: data.id,
        objectKind:
          data.kind === "folder" || data.path.endsWith("/") ? "folder" : "file",
        class: data.class,
        extension: extensionOf(data.path),
      });
      options?.onSuccess?.(...args);
    },
  });
}
